import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { TbEdit } from 'react-icons/tb';
import { useNavigate, useParams } from 'react-router-dom';

import { useApproveInactifRestaurantMutation, useFetchRestaurantQuery, useRejectInactifRestaurantMutation } from '@/api/hooks/useRestaurant';

import { Spinner } from '@/components/atoms/Spinner';
import { Typography } from '@/components/atoms/Typography';
import { Button } from '@/components/molecules/Button';
import { RestaurantStatus } from '@/components/molecules/RestaurantStatus';
import { SeeRestaurantPending } from '@/components/organisms/SeeRestaurantPending';

export const ModerationRestaurantPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const restaurantId = id || '';

  const [showRejectOptions, setShowRejectOptions] = useState(false);
  const [customComment, setCustomComment] = useState(false);
  const [comment, setComment] = useState('');
  const [predefinedComment, setPredefinedComment] = useState('');

  const { data: restaurant, isLoading } = useFetchRestaurantQuery({ id: restaurantId, withMenu: false }, { enabled: !!restaurantId });

  const { isLoading: isApprovingRestaurant, mutate: approveRestaurant } = useApproveInactifRestaurantMutation({
    onSuccess: () => {
      toast.success('Restaurant approuvé', {
        position: 'bottom-right'
      });
      navigate('/?tab=pending');
    }
  });

  const { isLoading: isRejectingRestaurant, mutate: rejectRestaurant } = useRejectInactifRestaurantMutation({
    onSuccess: () => {
      toast.success('Restaurant rejeté', {
        position: 'bottom-right'
      });
      navigate('/?tab=pending');
    }
  });

  const handleApprove = () => {
    approveRestaurant({ restaurantId });
  };

  const handleRejectClick = () => {
    setShowRejectOptions(true);
  };

  const handleConfirmReject = () => {
    const finalComment = customComment ? comment : predefinedComment;
    if (finalComment) {
      rejectRestaurant({ restaurantId, comment: finalComment });
      setShowRejectOptions(false);
      setCustomComment(false);
      setComment('');
      setPredefinedComment('');
    } else {
      toast.error('Une raison est requise pour rejeter le restaurant', {
        position: 'bottom-right'
      });
    }
  };

  const handleCancelReject = () => {
    setShowRejectOptions(false);
    setCustomComment(false);
    setComment('');
    setPredefinedComment('');
  };

  if (isLoading || !restaurant || !restaurantId) {
    return (
      <div className="flex flex-auto justify-center items-center h-48">
        <Spinner reverse={true} big />
      </div>
    );
  }

  const noReasonSelected = !predefinedComment && !comment;

  const buttonsLoading = isApprovingRestaurant || isRejectingRestaurant;

  return (
    <>
      <section className="bg-primary">
        <section className="mb-5 -mt-14 pt-32 md:pt-44 p-8 md:p-14 flex bg-primary w-full flex-col relative">
          <Typography variant="heroTitle" align="center" color="white">
            Est-ce que ce restaurant est conforme à nos règles ?
          </Typography>
        </section>

        <section className="bg-white md:rounded-t-[60px] rounded-t-[30px] pt-5 md:pt-14 pb-40 w-full">
          <div className="md:max-w-[90%] md:p-0 p-4 mx-auto">
            <SeeRestaurantPending restaurantData={restaurant} />
          </div>
        </section>
      </section>

      <div className="w-full fixed bottom-0">
        <div className="flex flex-col gap-4 xl:w-2/6 md:w-3/6 p-4 md:px-10 bg-primary justify-center rounded-t-[30px] border-x border-primary/10 font-bold mx-auto">
          {restaurant.validationStatus === 'PENDING' && (
            <>
              {showRejectOptions ? (
                <>
                  {customComment ? (
                    <input
                      type="text"
                      placeholder="Indiquez la raison du rejet"
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      className="p-2 rounded border border-gray-300 w-full"
                    />
                  ) : (
                    <div className="flex gap-5 items-center">
                      <select
                        value={predefinedComment}
                        onChange={(e) => setPredefinedComment(e.target.value)}
                        className="p-2 col-span-3 rounded border border-gray-300 w-64"
                      >
                        <option value="">Choisissez une raison de rejet</option>
                        <option value="Déclaration sur l'honneur manquant">Déclaration sur l'honneur manquant</option>
                        <option value="Restaurant pas halal">Restaurant pas halal</option>
                        <option value="Informations manquantes ou incorrectes">Informations manquantes ou incorrectes</option>
                        <option value="Contenu inapproprié">Contenu inapproprié</option>
                        <option value="Problème de localisation">Problème de localisation</option>
                      </select>
                      <div className="text-white items-center text-xl flex justify-center">OU</div>
                      {!customComment && (
                        <Button onClick={() => setCustomComment(true)} icon={<TbEdit size={20} />} color="black" fullWidth label="Écrire une raison" />
                      )}
                    </div>
                  )}
                  <div className="flex gap-4 mt-3">
                    <Button loading={buttonsLoading} disabled={noReasonSelected} onClick={handleConfirmReject} fullWidth color="black" label="Pas conforme" />
                    <Button loading={buttonsLoading} onClick={handleCancelReject} fullWidth color="gray" label="Annuler" />
                  </div>
                </>
              ) : (
                <div className="flex gap-4">
                  <Button loading={buttonsLoading} onClick={handleApprove} fullWidth color="success" label="Conforme" />
                  <Button loading={buttonsLoading} onClick={handleRejectClick} fullWidth color="black" label="Pas conforme" />
                </div>
              )}
            </>
          )}

          {restaurant.validationStatus === 'REJECTED' && (
            <div className="flex items-center justify-center flex-col">
              <Typography variant="h4" align="center" weight="bold" color="white">
                Ce restaurant a déjà été rejeté.
              </Typography>
            </div>
          )}

          {restaurant.validationStatus === 'ACCEPTED' && (
            <div className="flex items-center justify-center flex-col">
              <div className="flex items-center gap-3 ">
                <RestaurantStatus status={restaurant.validationStatus} circleOnly />
                <Typography variant="h4" align="center" weight="bold" color="white">
                  Restaurant en ligne sur mon-resto-halal.com
                </Typography>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
